import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import CookieCategory from './components/CookieCategory';

const cookieCategories = [
  {
    id: 'essential',
    icon: 'Lock',
    title: 'Essential Cookies',
    description: 'These cookies are strictly necessary for the platform to function. They enable core features such as authentication, session management, and security. These cannot be disabled.',
    required: true,
    defaultEnabled: true,
    cookies: [
      { name: 'sb-access-token', provider: 'ConstructionPro', purpose: 'Authentication session token', expiry: 'Session' },
      { name: 'sb-refresh-token', provider: 'ConstructionPro', purpose: 'Session refresh and renewal', expiry: '1 week' },
      { name: 'csrf_token', provider: 'ConstructionPro', purpose: 'Cross-site request forgery protection', expiry: 'Session' },
    ],
  },
  {
    id: 'functional',
    icon: 'Settings',
    title: 'Functional Cookies',
    description: 'These cookies enable enhanced functionality and personalization, such as remembering your preferences, display settings, and table configurations across sessions.',
    required: false,
    defaultEnabled: true,
    cookies: [
      { name: 'user_preferences', provider: 'ConstructionPro', purpose: 'Stores UI preferences and layout settings', expiry: '1 year' },
      { name: 'table_config', provider: 'ConstructionPro', purpose: 'Remembers table column and sort preferences', expiry: '6 months' },
    ],
  },
  {
    id: 'analytics',
    icon: 'BarChart2',
    title: 'Analytics Cookies',
    description: 'These cookies help us understand how users interact with the platform. The data collected is aggregated and anonymous, used to improve performance and user experience.',
    required: false,
    defaultEnabled: false,
    cookies: [
      { name: '_ga', provider: 'Google Analytics', purpose: 'Distinguishes unique users and sessions', expiry: '2 years' },
      { name: '_ga_*', provider: 'Google Analytics', purpose: 'Persists session state', expiry: '2 years' },
      { name: '_gid', provider: 'Google Analytics', purpose: 'Distinguishes users within a 24-hour period', expiry: '24 hours' },
    ],
  },
  {
    id: 'marketing',
    icon: 'Megaphone',
    title: 'Marketing Cookies',
    description: 'These cookies are used to track visitors across websites to display relevant advertisements. They are set by third-party advertising partners.',
    required: false,
    defaultEnabled: false,
    cookies: [
      { name: '_fbp', provider: 'Meta (Facebook)', purpose: 'Tracks visits for advertising purposes', expiry: '3 months' },
      { name: 'ads/ga-audiences', provider: 'Google Ads', purpose: 'Re-engagement of users via Google Ads', expiry: 'Session' },
    ],
  },
];

const CookiePolicy = () => {
  const currentYear = new Date()?.getFullYear();
  const [saved, setSaved] = useState(false);

  const handleSavePreferences = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-900 hover:text-accent transition-colors">
            <Icon name="HardHat" size={24} className="text-accent" />
            <span className="font-semibold text-lg">ConstructionPro</span>
          </Link>
          <nav className="flex items-center gap-4 text-sm text-gray-500">
            <Link to="/privacy-policy" className="hover:text-gray-900 transition-colors">Privacy Policy</Link>
            <Link to="/legal-terms" className="hover:text-gray-900 transition-colors">Legal</Link>
          </nav>
        </div>
      </header>
      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-6">
          {/* Title Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                <Icon name="Cookie" size={20} className="text-accent" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Cookie Policy</h1>
            </div>
            <p className="text-gray-500 text-sm mb-6">Last updated: January 1, 2025 &nbsp;&bull;&nbsp; Version 1.0</p>
            <p className="text-gray-700 leading-relaxed">
              ConstructionPro uses cookies and similar tracking technologies to enhance your experience, analyze platform usage, and support our services. This policy explains what cookies we use, why we use them, and how you can manage your preferences.
            </p>
          </div>

          {/* What Are Cookies */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">What Are Cookies?</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Cookies are small text files placed on your device when you visit a website. They are widely used to make websites work efficiently, remember your preferences, and provide information to site owners.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: 'Clock', label: 'Session Cookies', desc: 'Deleted when you close your browser' },
                { icon: 'Calendar', label: 'Persistent Cookies', desc: 'Remain until expiry date or manual deletion' },
                { icon: 'Globe', label: 'Third-Party Cookies', desc: 'Set by external services we use' },
              ]?.map((item) => (
                <div key={item?.label} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <Icon name={item?.icon} size={18} className="text-accent mb-2" />
                  <p className="font-medium text-gray-900 text-sm mb-1">{item?.label}</p>
                  <p className="text-xs text-gray-500">{item?.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Cookie Preference Center */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Cookie Preference Center</h2>
                <p className="text-sm text-gray-500 mt-1">Manage your consent for each cookie category below.</p>
              </div>
              <button
                onClick={handleSavePreferences}
                className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors"
              >
                {saved ? (
                  <><Icon name="CheckCircle" size={15} /> Saved!</>
                ) : (
                  <><Icon name="Save" size={15} /> Save Preferences</>
                )}
              </button>
            </div>
            <div className="space-y-4">
              {cookieCategories?.map((cat) => (
                <CookieCategory key={cat?.id} {...cat} />
              ))}
            </div>
          </div>

          {/* How to Control Cookies */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">How to Control Cookies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              In addition to the preference center above, you can control cookies through your browser settings. Note that disabling certain cookies may affect platform functionality.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { browser: 'Google Chrome', url: 'https://support.google.com/chrome/answer/95647' },
                { browser: 'Mozilla Firefox', url: 'https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer' },
                { browser: 'Safari', url: 'https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac' },
                { browser: 'Microsoft Edge', url: 'https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09' },
              ]?.map((item) => (
                <a
                  key={item?.browser}
                  href={item?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <span>{item?.browser} Cookie Settings</span>
                  <Icon name="ExternalLink" size={14} className="text-gray-400" />
                </a>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Questions About Cookies?</h2>
            <p className="text-gray-600 text-sm mb-2">Contact our privacy team:</p>
            <div className="space-y-1 text-sm text-gray-700">
              <p><strong>Email:</strong> <a href="mailto:privacy@constructionpro.com" className="text-accent hover:underline">privacy@constructionpro.com</a></p>
              <p><strong>Address:</strong> ConstructionPro, 123 Build Street, Suite 400, New York, NY 10001</p>
            </div>
          </div>
        </div>
      </main>
      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white mt-8">
        <div className="max-w-4xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-gray-500">
          <span>Copyright {currentYear} ConstructionPro. All rights reserved.</span>
          <nav className="flex items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-gray-900 transition-colors">Privacy Policy</Link>
            <span className="text-gray-300">|</span>
            <Link to="/legal-terms" className="hover:text-gray-900 transition-colors">Legal</Link>
            <span className="text-gray-300">|</span>
            <Link to="/cookie-policy" className="hover:text-gray-900 transition-colors font-medium text-accent">Cookie Policy</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default CookiePolicy;
